import { useCallback } from 'react'
import { usePriceIntentValueOrThrow } from '@/features/priceCalculator/usePriceIntent'
import { useExternalInsurerUpdateMutation } from '@/services/graphql/generated'
import { type ExternalInsurer } from '@/services/graphql/graphql'
import { logger } from '@/services/logger/logger.client'
import { type ApolloError } from '@apollo/client'

type UseChangeExternalInsurerParams =
  | {
      onCompleted?: (externalInsurer?: ExternalInsurer) => void
      onError?: (error: ApolloError) => void
    }
  | undefined

export function useChangeExternalInsurer(params: UseChangeExternalInsurerParams = {}) {
  const priceIntent = usePriceIntentValueOrThrow()
  const priceIntentId = priceIntent.id

  const [updateExternalInsurer, { loading }] = useExternalInsurerUpdateMutation({
    onCompleted(data) {
      const externalInsurer = data.priceIntentExternalInsurerUpdate.priceIntent?.externalInsurer
      if (externalInsurer) {
        logger.info('Added external insurer', {
          priceIntentId,
          externalInsurerId: externalInsurer.id,
        })
      } else {
        logger.warn('Removed external insurer', {
          priceIntentId,
        })
      }
      params.onCompleted?.(externalInsurer ?? undefined)
    },
    onError(error) {
      logger.warn('Error updating external insurer', {
        priceIntentId,
        error,
      })
      params.onError?.(error)
    },
  })

  const changeExternalInsurer = useCallback(
    (externalInsurerId?: string) => {
      updateExternalInsurer({ variables: { priceIntentId, externalInsurerId } })
    },
    [priceIntentId, updateExternalInsurer],
  )

  return [changeExternalInsurer, loading] as const
}
